import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Lock, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidationError("Missing reset token. Please use the link from your email.");
      return;
    }
    let cancelled = false;
    api.get(`/auth/password-reset/validate?token=${encodeURIComponent(token)}`)
      .catch((err) => {
        if (!cancelled) {
          setValidationError(
            formatApiErrorDetail(err.response?.data?.detail)
            || "This reset link is invalid or expired."
          );
        }
      })
      .finally(() => { if (!cancelled) setValidating(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-10 bg-slate-50/40" data-testid="reset-password-page">
      <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-xl nc-gradient mx-auto flex items-center justify-center text-white font-bold text-xl">N</div>

        {validating && (
          <div className="mt-6 text-center" data-testid="reset-validating">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
            <p className="mt-2 text-sm text-slate-500">Checking your reset link…</p>
          </div>
        )}

        {!validating && validationError && (
          <div className="mt-6 text-center" data-testid="reset-invalid">
            <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-700 mx-auto flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h2 className="mt-3 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Link unavailable</h2>
            <p className="mt-2 text-sm text-slate-600">{validationError}</p>
            <Link to="/forgot-password" className="mt-5 inline-block nc-btn-primary">Request a new link</Link>
          </div>
        )}

        {!validating && !validationError && !done && (
          <>
            <h1 className="mt-4 text-center text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
              Set a new password
            </h1>
            <p className="text-center text-sm text-slate-500 mt-1">
              Choose a password you don't use elsewhere.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">New password</label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    className="nc-input pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-password-input"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">At least 8 characters.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Confirm password</label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    className="nc-input pl-9"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={submitting} className="nc-btn-primary w-full justify-center" data-testid="reset-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Update password
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="mt-6 text-center" data-testid="reset-done">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h2 className="mt-3 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Password updated</h2>
            <p className="mt-2 text-sm text-slate-600">Redirecting you to login…</p>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-slate-600">
          <Link to="/login" className="inline-flex items-center gap-1 text-slate-600 hover:text-emerald-700" data-testid="reset-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
